import {
  getLegacyDaypartsAvailability,
  getDaypartAvailability,
  checkCategoryAvailability,
} from './daypartMenuAvailability';
import flattenChoiceSelections from './flattenChoiceSelections';
import itemCategoryAvailibilityValidate from './itemCategoryAvailibilityValidate';

export default (
  purchase?: PurchaseWithTotals,
  items?: Items,
  daypartConfig?: any,
  dateTimeSelected?: string,
  daypartSlots?: DaypartMap,
  subMenus?: MenuNode[],
  categoriesInfo?: CategoryInfo[],
): boolean => {
  if (!purchase) return false;
  if (!items) return false;

  const { item: rootItem, choiceSelections, sizeId } = purchase;

  if (subMenus && categoriesInfo) {
    const categoryAvail = itemCategoryAvailibilityValidate({
      id: rootItem.id,
      subMenus,
      categoriesInfo,
      dateTimeSelected,
      daypartSlots,
    });

    if (!categoryAvail) return false;
  }

  const dayPartTimeBracket = daypartConfig
    ? daypartConfig.dayPartTimeBracket
    : 0;
  const isComposite = !!rootItem.sizes.length;
  const item =
    (isComposite && rootItem.sizes.find(s => s.id === sizeId)) || rootItem;

  const { availability, days, start, end, dayparts } = item;

  let stagePurchaseAvail: boolean;

  if (dayparts && dayparts.length > 0 && daypartSlots) {
    stagePurchaseAvail = getDaypartAvailability({
      dayparts,
      daypartSlots,
      dateTimeSelected,
    });
  } else {
    stagePurchaseAvail = getLegacyDaypartsAvailability({
      availability,
      days,
      start,
      end,
      timeBracket: dayPartTimeBracket,
      dateTimeSelected,
    });
  }

  if (!stagePurchaseAvail) return false;

  // Check top-level choices via choicesWithQuantity. This covers choices that have
  // a non-zero quantity from server-side menu defaults and may not appear in
  // choiceSelections, which isChoiceSelectionsDayPartAvailable would miss.
  const topLevelChoicesAvail = purchase.choicesWithQuantity.every(c => {
    const choice = items[c.id];
    if (!choice) return true;
    const { availability, days, start, end, dayparts } = choice;
    if (dayparts && dayparts.length > 0 && daypartSlots) {
      return getDaypartAvailability({
        dayparts,
        daypartSlots,
        dateTimeSelected,
      });
    }
    return getLegacyDaypartsAvailability({
      availability,
      days,
      start,
      end,
      timeBracket: dayPartTimeBracket,
      dateTimeSelected,
    });
  });

  if (!topLevelChoicesAvail) return false;

  // Check nested choices via choiceSelections to catch items at any nesting depth
  // that choicesWithQuantity doesn't flatten deeply (the CNS-904 addition).
  return isChoiceSelectionsDayPartAvailable({
    choiceSelections: choiceSelections as NestedChoiceSelection,
    items,
    daypartSlots,
    dayPartTimeBracket,
    categoriesInfo,
    dateTimeSelected,
  });
};

export const isChoiceSelectionsDayPartAvailable = ({
  choiceSelections,
  items,
  daypartSlots,
  dayPartTimeBracket,
  categoriesInfo,
  dateTimeSelected,
}: {
  choiceSelections: NestedChoiceSelection;
  items: Items;
  daypartSlots?: DaypartMap;
  dayPartTimeBracket?: number;
  categoriesInfo?: CategoryInfo[];
  dateTimeSelected?: string;
}) => {
  const choices = flattenChoiceSelections({ choiceSelections });

  const allChoicesAvail = [...choices].every(id => {
    const itemFromChoice = items[id];

    if (!itemFromChoice) return true;

    const { availability, days, start, end, dayparts, mimCategoryId } =
      itemFromChoice;

    if (categoriesInfo) {
      const category = categoriesInfo.find(cat => cat.id === mimCategoryId);
      const [categoryIsAvailable] = checkCategoryAvailability({
        category,
        daypartSlots,
        dateTimeSelected,
      });

      if (!categoryIsAvailable) return false;
    }

    if (!dayparts || dayparts.length === 0) {
      return getLegacyDaypartsAvailability({
        availability,
        days,
        start,
        end,
        timeBracket: dayPartTimeBracket,
        dateTimeSelected,
      });
    }

    if (!daypartSlots) {
      return true;
    }

    return getDaypartAvailability({ dayparts, daypartSlots, dateTimeSelected });
  });

  return allChoicesAvail;
};
